import React from 'react';
import { Workflow, WorkflowStatus } from '../../types/workflow';

const STATUS_STYLES: Record<WorkflowStatus, { bg: string; color: string; label: string }> = {
  healthy:  { bg: '#E9F6F6', color: '#0F6E56', label: 'Healthy' },
  degraded: { bg: '#FEF3C7', color: '#92400e', label: 'Degraded' },
  paused:   { bg: '#F5F7FA', color: '#595A60', label: 'Paused' },
  failing:  { bg: '#FEE2E2', color: '#b91c1c', label: 'Failing' },
};

const SLA_STYLES: Record<string, { bg: string; color: string; label: string }> = {
  'on-track':  { bg: '#E9F6F6', color: '#0F6E56', label: 'SLA on track' },
  'at-risk':   { bg: '#FEF3C7', color: '#92400e', label: 'SLA at risk' },
  'breached':  { bg: '#FEE2E2', color: '#b91c1c', label: 'SLA breached' },
};

interface Props {
  workflow: Workflow;
  onRun: () => void;
  running: boolean;
  onAction: (label: string) => void;
}

export function PersistentHeader({ workflow, onRun, running, onAction }: Props) {
  const status = STATUS_STYLES[workflow.status];
  const sla = SLA_STYLES[workflow.sla_status];

  return (
    <div className="bg-white border-b border-trim px-8 pt-6 pb-4 flex-shrink-0">
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-lg font-semibold text-ink truncate">{workflow.name}</h1>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: status.bg, color: status.color }}>{status.label}</span>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: sla.bg, color: sla.color }}>{sla.label}</span>
            <span className="text-xs text-muted font-mono">{workflow.id}</span>
          </div>
          <div className="flex items-center gap-5 mt-2 text-xs text-muted flex-wrap">
            <span><span className="text-copy">Env:</span> <span className="uppercase font-medium">{workflow.environment}</span></span>
            <span><span className="text-copy">Version:</span> <span className="font-mono text-ink">{workflow.version}</span></span>
            <span><span className="text-copy">Owner:</span> <span className="text-ink">{workflow.owner}</span></span>
            <span><span className="text-copy">Active runs:</span> <span className="text-ink font-medium">{workflow.active_runs}</span></span>
            <span><span className="text-copy">Last deployed:</span> <span className="text-ink">{new Date(workflow.last_deployed_at).toLocaleString()}</span></span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onRun}
            disabled={running}
            className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
            style={{
              background: running ? '#A0A3AB' : '#14b8a6',
              color: 'white',
              cursor: running ? 'wait' : 'pointer',
            }}
          >
            {running ? 'Running…' : 'Run Workflow'}
          </button>
          {['Pause', 'Edit', 'Clone', 'Export', 'Compare'].map(label => (
            <button
              key={label}
              onClick={() => onAction(label)}
              className="px-3 py-1.5 text-xs font-medium text-copy border border-trim rounded-lg hover:bg-wash transition-colors"
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
